/**
 * releaseStamp - reads the build identity stamped onto the document root.
 *
 * `scripts/with-release.js` stamps every build (and every dev server start)
 * onto <html> as `data-release` and `data-timestamp`:
 *
 *	 <html data-release="85a10450-…" data-timestamp="2026-09-01-14-43-26">
 *
 * The DOM is read rather than `process.env.REACT_APP_RELEASE_ID` on purpose:
 * whatever is displayed then always matches what a developer sees at the top
 * of the Elements panel, so the two can never disagree.
 */

export interface ReleaseStamp {
	/** Unique per build artifact — two builds of the same commit differ. */
	release: string;
	/** Build time in UTC, formatted YYYY-MM-DD-HH-MM-SS. */
	timestamp: string;
}

/**
 * Returns the stamp, or null when there isn't a real one to show.
 *
 * Two cases legitimately come back null and must not render anything:
 *	 - Jest, which never loads public/index.html, so the attributes are absent.
 *	 - A build run through `react-scripts` directly instead of `npm run build`,
 *	   which leaves the placeholder as the literal text "%REACT_APP_RELEASE_ID%"
 *	   (CRA passes unknown placeholders through rather than blanking them).
 *
 * Showing "%REACT_APP_RELEASE_ID%" to a user would be worse than showing
 * nothing, hence the leading-% check.
 */
export const getReleaseStamp = (): ReleaseStamp | null => {
	if (typeof document === 'undefined') return null;

	const { release, timestamp } = document.documentElement.dataset;

	const isReal = (value?: string): value is string =>
		Boolean(value) && !value!.startsWith('%');

	if (!isReal(release) || !isReal(timestamp)) return null;

	return { release, timestamp };
};
